import {
  Col,
  ConfigProvider,
  Divider,
  Flex,
  Form,
  message,
  Row,
  Space,
  Tooltip,
} from 'antd';
import CustomCheckboxGroup from '../../components/CustomCheckboxGroup';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getTeamsDDLOFFeature } from '@/common/utils';
import {
  countRowsByProviderDisplayName,
  formatTransportProviderOptionLabel,
  shouldShowProviderTeamLabel,
  teamsForTransportProviderLabel,
} from '@/common/utils/transportProviderOptionLabel';
import {
  AddItemButton,
  CustomButton,
  CustomDynamicSelect,
  CustomInput,
  CustomShipmentText,
  CustomTitle,
  CustomDatePicker,
  CustomTimePicker,
  TeamDropdownSelect,
  TruncateWithTooltip,
} from '../../components';
import { useSpin } from '../../components/SpinContext';
import { UpdateShipment, ReassignShipmentCarrier } from '../../apis/shipment';
import { useToken } from '../../common/helper';
import { GetSettingsCompanySensors, SensorData } from '../../apis/settings';
import { CourierData } from '@/apis/lookup';
import { getCompanyCouriersDDL, getLocationDDL } from '@/common/utils/dropDown';
import {
  ErrorLike,
  MenuItemData,
  SingleShipmentDataExtended,
} from '../../apis/types';
import { getPermissions } from '../../common/helper/getPermissions';
import { AddSettingsLocation } from '../../apis/settings';
import SaveLocationModal from '../Modals/SaveLocationModal';
import LocationAutoCompleteShipment from '../Modals/LocationAutoCompleteShipment';
import { GoogleMapsProvider } from '../../components/GoogleMapsProvider';
import theme from '@/assets/theme';
import { themeT } from '@/utils/themeText';
import { DeleteIcon, LeftTailArrowIcon } from '@/assets/icons/icons';
import { CloseOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { useFetchSingleShipment } from '@/hooks';
import { getUserTimeZoneValue } from '@/common/utils/utils';
import useUnauthorize from '@/hooks/useUnauthorize';
import { getTimeZoneAbbreviation } from '@/common/utils/getFormattedTimeUtils';
import {
  initialSensorRowsFromShipment,
  type SensorFormRow,
} from './sensorFormPayload';
import {
  fetchTiveAutocreateSensorId,
  hasCoordinates,
  hasEligibleTiveRow,
  linkedTrackingIds,
  runTiveAutocreate,
  TIVE_ADDRESS_REQUIRED_MESSAGE,
} from './tiveAutocreate';
import {
  applyShipmentDateEdits,
  buildShipmentDateFormValues,
  SHIPMENT_DATE_FORM_PAIRS,
} from './shipmentDateFormFields';
import CustomDeleteModal from '../Modals/CustomDeleteModal';
import { useSensorProductLinksGate } from '@/hooks/useSensorProductLinksGate';
import EditProductsAndSensors from './components/EditProductsAndSensors';
import EditProductFormView from './components/EditProductFormView';
import { editSensorsPayload } from './editShipmentPayload';
import EditShipmentFooter from './components/EditShipmentFooter';
import { useEditSensorsAndProducts } from './useEditSensorsAndProducts';
import styles from './EditShipmentDrawerWithTeams.module.scss';

interface EditShipmentDrawerWithTeamsProps {
  toggleDrawer: (value: boolean, name?: string) => void;
  shipmentId: string;
  onEditShipmentSuccess: (data: unknown) => void;
}

/**
 * Component for editing shipment details
 *
 * @param toggleDrawer - Function to toggle drawer visibility
 * @param shipmentId - ID of the shipment to edit
 * @param onEditShipmentSuccess - Callback function called after successful edit
 */
interface Team {
  id: string;
  name: string;
  color?: string;
}

type SensorSelectRow = SensorData & { sensor_label: string };
type CourierSelectRow = CourierData & { courier_label: string };

interface LocationOption {
  id?: string;
  name?: string;
  placeName?: string;
  address?: string;
  latitude?: number;
  longitude?: number;
  polygon?: Array<{ lat: number; lng: number }>;
  zip_code?: string;
  country?: string;
}

/** Form items in the transport row sit flush, like the rest of the drawer's two-column rows. */
const FLUSH_FORM_ITEM = { components: { Form: { itemMarginBottom: 0 } } };

const EditShipmentDrawerWithTeams: React.FC<
  EditShipmentDrawerWithTeamsProps
> = ({ toggleDrawer, shipmentId, onEditShipmentSuccess }) => {
  const { showSpin, hideSpin } = useSpin();
  const [form] = Form.useForm();
  const { token, decodedUserToken } = useToken();
  const { unauthorize } = useUnauthorize();
  const [shipment, setShipment] = useState<SingleShipmentDataExtended | null>(
    null
  );
  const [sensorsList, setSensorsList] = useState<SensorSelectRow[]>([]);
  const [tiveAutocreateSensorId, setTiveAutocreateSensorId] = useState('');
  const [createInTive, setCreateInTive] = useState(false);
  const [couriersList, setCouriersList] = useState<CourierSelectRow[]>([]);
  const [allTeams, setAllTeams] = useState<Team[]>([]);
  const [selectedTeams, setSelectedTeams] = useState<Team[]>([]);
  const [teamsSearch, setTeamsSearch] = useState('');
  const [debouncedTeamsSearch, setDebouncedTeamsSearch] = useState('');
  const [reassignConfirmOpen, setReassignConfirmOpen] = useState(false);
  const [pendingSubmitModel, setPendingSubmitModel] = useState<Record<
    string,
    unknown
  > | null>(null);
  const [locationList, setLocationList] = useState<LocationOption[]>([]);
  const [fromLocation, setFromLocation] = useState<LocationOption | null>(null);
  const [toLocation, setToLocation] = useState<LocationOption | null>(null);
  const [fromDisplayValue, setFromDisplayValue] = useState<string>('');
  const [toDisplayValue, setToDisplayValue] = useState<string>('');
  const [fromEditMode, setFromEditMode] = useState(false);
  const [toEditMode, setToEditMode] = useState(false);
  const [saveLocationModalOpen, setSaveLocationModalOpen] = useState(false);
  const [saveLocationTarget, setSaveLocationTarget] = useState<
    'from' | 'to' | null
  >(null);
  const fromLocationRef = useRef<LocationOption | null>(null);
  const toLocationRef = useRef<LocationOption | null>(null);
  const fromLatLngRef = useRef<{ lat: number | null; lng: number | null }>({
    lat: null,
    lng: null,
  });
  const toLatLngRef = useRef<{ lat: number | null; lng: number | null }>({
    lat: null,
    lng: null,
  });
  const { shipmentData } = useFetchSingleShipment(shipmentId, true);
  const linksEnabled = useSensorProductLinksGate();
  const [drawerView, setDrawerView] = useState<'shipment' | 'product_form'>(
    'shipment'
  );
  const [viewShipmentId, setViewShipmentId] = useState(shipmentId);
  if (viewShipmentId !== shipmentId) {
    setViewShipmentId(shipmentId);
    setDrawerView('shipment');
  }
  const links = useEditSensorsAndProducts({
    shipmentId,
    shipment,
    sensorsList,
    token: token ?? '',
    unauthorize,
    enabled: linksEnabled,
    openProductForm: () => setDrawerView('product_form'),
  });
  const decodedToken = decodedUserToken();
  const timeZone = getUserTimeZoneValue(decodedToken ?? undefined);
  interface RootState {
    teams: {
      list: Team[];
    };
    menu: {
      items: MenuItemData[];
    };
  }

  const setDateTimeFields = useCallback(
    (shipmentData: SingleShipmentDataExtended) => {
      const dateFormat = decodedToken?.date_format as string | undefined;
      form.setFieldsValue(
        buildShipmentDateFormValues(shipmentData, dateFormat)
      );
    },
    [form, decodedToken?.date_format]
  );
  useEffect(() => {
    if (shipmentData) {
      setShipment(shipmentData);
      setDateTimeFields(shipmentData);
    }
  }, [shipmentData, setDateTimeFields]);

  const teamsList = useSelector((state: RootState) => state.teams.list);
  const main_menu = useSelector((state: RootState) => state.menu.items);
  const LocationsPermissions = getPermissions(
    main_menu,
    'Settings',
    'Locations'
  );
  const canCreateLocation = LocationsPermissions?.is_create ?? false;
  const isArchived = Boolean(shipment?.is_archived);
  const sensorRowsWatch = Form.useWatch('sensor_rows', form) as
    SensorFormRow[] | undefined;
  const canCreateInTive = hasEligibleTiveRow(
    linksEnabled ? links.sensorRows : sensorRowsWatch,
    sensorsList,
    tiveAutocreateSensorId,
    linkedTrackingIds(shipment?.sensors)
  );

  useEffect(() => {
    fetchTiveAutocreateSensorId(token ?? '')
      .then(setTiveAutocreateSensorId)
      .catch(console.error);
  }, [token]);

  useEffect(() => {
    if (!canCreateInTive) {
      setCreateInTive(false);
    }
  }, [canCreateInTive]);

  useEffect(() => {
    fromLocationRef.current = fromLocation;
  }, [fromLocation]);
  useEffect(() => {
    toLocationRef.current = toLocation;
  }, [toLocation]);

  const hasValidTeams = !(
    teamsList.length === 0 ||
    (teamsList.length === 1 && teamsList[0].name.toLowerCase() === 'default')
  );

  const formatDateWithTZ = (date?: string | null) => {
    if (!date) return '--';
    const tz = getTimeZoneAbbreviation(date, timeZone);
    return `${date} ${tz ? `${tz}` : ''}`;
  };

  const fetchSensors = useCallback(() => {
    // Use v2 list (same as Create / Settings). V1 filters teams__in only and
    // drops is_all_teams credentials that have an empty teams M2M — which is
    // how Settings "ALL" sensors are stored.
    GetSettingsCompanySensors(token ?? '')
      .then((res) => {
        const data = res?.data || null;
        if (data) {
          if (data.data?.is_success) {
            if (data.data.data && data.data.data.length > 0) {
              const sensorRows = data.data.data as SensorData[];
              const nameCounts = countRowsByProviderDisplayName(
                sensorRows,
                (r) => r.sensor_name
              );
              setSensorsList(
                sensorRows.map((row) => {
                  const showTeams = shouldShowProviderTeamLabel(
                    row.sensor_name,
                    nameCounts
                  );
                  return {
                    ...row,
                    sensor_label: formatTransportProviderOptionLabel(
                      row.sensor_name,
                      teamsForTransportProviderLabel(row),
                      { showTeamDisambiguation: showTeams }
                    ),
                  };
                })
              );
            }
          } else {
            message.error(data?.data?.message);
          }
        }
      })
      .catch((error) => {
        unauthorize(error);
      })
      .finally(() => {});
  }, [token, unauthorize]);

  useEffect(() => {
    if (!hasValidTeams) return;

    const timer = window.setTimeout(() => {
      setDebouncedTeamsSearch(teamsSearch);
    }, 400);

    return () => window.clearTimeout(timer);
  }, [hasValidTeams, teamsSearch]);

  useEffect(() => {
    if (!hasValidTeams) return;

    let cancelled = false;
    const teamsRequest = debouncedTeamsSearch
      ? getTeamsDDLOFFeature(token ?? '', 'Shipment', debouncedTeamsSearch)
      : getTeamsDDLOFFeature(token ?? '', 'Shipment');

    teamsRequest
      .then((val) => {
        if (cancelled) return;
        const teams = val || [];
        setAllTeams(teams);
      })
      .catch(console.error);
    return () => {
      cancelled = true;
    };
  }, [debouncedTeamsSearch, token, hasValidTeams]);

  const handleTeamsSearch = useCallback((val: string) => {
    setTeamsSearch(val);
  }, []);

  useEffect(() => {
    fetchSensors();
  }, [fetchSensors]);

  useEffect(() => {
    if (!token) return;
    getLocationDDL(token, true)
      .then((val) => {
        if (val && val.length > 0) {
          setLocationList(val);
        }
      })
      .catch((error) => {
        unauthorize(error);
      });
  }, [token, unauthorize]);

  const handleAddressPlaceSelect = useCallback(
    (
      place: LocationOption & { address?: string },
      fieldName: string,
      options?: { isDetailEnrichment?: boolean }
    ): void => {
      const lat = place?.latitude != null ? Number(place.latitude) : null;
      const lng = place?.longitude != null ? Number(place.longitude) : null;
      const displayName =
        place.id && place.address && !place.placeName
          ? `${place.name} · ${place.address}`
          : place.placeName || place.name || place.address || '';
      if (fieldName === 'from_location') {
        const prev = fromLocationRef.current;
        // Only explicit detail enrichment keeps edit-mode open — never a fresh
        // lightweight reselect of the same id with null/missing latitude.
        const sameIdEnrichment =
          !!options?.isDetailEnrichment && !!prev?.id && prev.id === place.id;
        fromLatLngRef.current = { lat, lng };
        fromLocationRef.current = place;
        setFromLocation(place);
        setFromDisplayValue(displayName);
        if (!sameIdEnrichment) {
          setFromEditMode(false);
        }
      } else if (fieldName === 'to_location') {
        const prev = toLocationRef.current;
        const sameIdEnrichment =
          !!options?.isDetailEnrichment && !!prev?.id && prev.id === place.id;
        toLatLngRef.current = { lat, lng };
        toLocationRef.current = place;
        setToLocation(place);
        setToDisplayValue(displayName);
        if (!sameIdEnrichment) {
          setToEditMode(false);
        }
      }
      form.setFieldsValue({ [fieldName]: displayName });
    },
    [form]
  );

  const shipmentLocationToOption = useCallback(
    (
      loc: NonNullable<SingleShipmentDataExtended['from_location']>
    ): LocationOption & { address?: string } => ({
      id: loc.id,
      name: loc.name,
      address: loc.address,
      latitude: loc.latlng?.lat,
      longitude: loc.latlng?.lng,
      // Some legacy LocationV2 rows store polygon as a JSON-encoded string
      // (e.g. "[]") instead of an array — mapping that crashed the drawer.
      polygon: Array.isArray(loc.polygon)
        ? loc.polygon.map((p) => ({
            lat: p.lat,
            lng: p.lng,
          }))
        : undefined,
    }),
    []
  );

  useEffect(() => {
    if (!shipment) return;
    if (shipment.from_location) {
      handleAddressPlaceSelect(
        shipmentLocationToOption(shipment.from_location),
        'from_location'
      );
    }
    if (shipment.to_location) {
      handleAddressPlaceSelect(
        shipmentLocationToOption(shipment.to_location),
        'to_location'
      );
    }
    // Prefill O/D once per shipment load — not on every location edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipment?.id]);

  const handleAddressInputChange = (
    target: 'from' | 'to',
    value: string
  ): void => {
    const loc =
      target === 'from' ? fromLocationRef.current : toLocationRef.current;
    if (!loc?.id) {
      if (target === 'from') {
        fromLatLngRef.current = { lat: null, lng: null };
        setFromDisplayValue(value);
      } else {
        toLatLngRef.current = { lat: null, lng: null };
        setToDisplayValue(value);
      }
      return;
    }
    const locWithAddress = loc as LocationOption & { address?: string };
    const expected =
      loc.id && locWithAddress.address && !loc.placeName
        ? `${loc.name} · ${locWithAddress.address}`
        : loc.placeName || loc.name || '';
    if (value !== expected) {
      if (target === 'from') {
        fromLatLngRef.current = { lat: null, lng: null };
        fromLocationRef.current = null;
        setFromLocation(null);
        setFromDisplayValue(value);
        setFromEditMode(false);
      } else {
        toLatLngRef.current = { lat: null, lng: null };
        toLocationRef.current = null;
        setToLocation(null);
        setToDisplayValue(value);
        setToEditMode(false);
      }
    }
  };

  const handleDetachLocation = (target: 'from' | 'to'): void => {
    const locRef =
      target === 'from' ? fromLocationRef.current : toLocationRef.current;
    const addressOnly =
      (locRef as LocationOption & { address?: string })?.address || '';
    const fieldName = target === 'from' ? 'from_location' : 'to_location';
    const detachLat = locRef?.latitude != null ? Number(locRef.latitude) : null;
    const detachLng =
      locRef?.longitude != null ? Number(locRef.longitude) : null;
    if (target === 'from') {
      fromLocationRef.current = null;
      setFromLocation(null);
      setFromDisplayValue(addressOnly);
      fromLatLngRef.current = { lat: detachLat, lng: detachLng };
    } else {
      toLocationRef.current = null;
      setToLocation(null);
      setToDisplayValue(addressOnly);
      toLatLngRef.current = { lat: detachLat, lng: detachLng };
    }
    form.setFieldsValue({ [fieldName]: addressOnly });
  };

  const handleBookmarkClick = (target: 'from' | 'to'): void => {
    const addressStr = target === 'from' ? fromDisplayValue : toDisplayValue;
    if (!addressStr) return;
    setSaveLocationTarget(target);
    setSaveLocationModalOpen(true);
  };

  const handleSaveLocation = async (locationName: string): Promise<void> => {
    const target = saveLocationTarget;
    if (!target) return;
    if (!token) {
      message.error('Unable to save location: missing auth token');
      return;
    }
    const fieldName = target === 'from' ? 'from_location' : 'to_location';
    const displayValue = target === 'from' ? fromDisplayValue : toDisplayValue;
    const locRef =
      target === 'from' ? fromLocationRef.current : toLocationRef.current;
    const addressStr = locRef?.address || displayValue;
    const latLng =
      target === 'from' ? fromLatLngRef.current : toLatLngRef.current;

    try {
      const res = await AddSettingsLocation(token, {
        name: locationName,
        address: addressStr,
        latitude: latLng.lat,
        longitude: latLng.lng,
        zip_code: locRef?.zip_code ?? '',
        country: locRef?.country ?? '',
        teams: selectedTeams.map((t) => t.id),
        polygon: [],
        contacts: [],
        receiving_hours: { start: null, end: null },
        location_type: null,
        id: null,
      });

      const resData = (
        res as {
          data?: {
            data?: {
              is_success?: boolean;
              data?: {
                created_locations?: LocationOption & { address?: string };
              };
            };
          };
        }
      )?.data?.data;
      if (!resData?.is_success) {
        throw new Error('API returned is_success: false');
      }

      const created = resData?.data?.created_locations;
      const newLoc: LocationOption & { address?: string } = {
        ...(created ?? {}),
        id: created?.id,
        name: created?.name ?? locationName,
        address: created?.address ?? addressStr,
        latitude: latLng.lat ?? undefined,
        longitude: latLng.lng ?? undefined,
      };

      setLocationList((prev) => [newLoc, ...prev]);
      getLocationDDL(token, true)
        .then((refreshed) => {
          if (refreshed?.length > 0) setLocationList(refreshed);
        })
        .catch(() => {
          /* silent — optimistic entry is already in list */
        });

      handleAddressPlaceSelect(newLoc, fieldName);
      setSaveLocationModalOpen(false);
      message.success('Location saved');
    } catch {
      // Keep modal open; do not rethrow (SaveLocationModal has no async catch).
      message.error('Failed to save location. Please try again.');
    }
  };

  // Load couriers for the editable carrier field.
  useEffect(() => {
    if (!token) return;
    getCompanyCouriersDDL(token)
      .then((val) => {
        if (!val?.length) return;
        const rows = val as CourierData[];
        const nameCounts = countRowsByProviderDisplayName(
          rows,
          (r) => r.courier_name
        );
        setCouriersList(
          rows.map((row) => ({
            ...row,
            courier_label: formatTransportProviderOptionLabel(
              row.courier_name,
              teamsForTransportProviderLabel(row),
              {
                showTeamDisambiguation: shouldShowProviderTeamLabel(
                  row.courier_name,
                  nameCounts
                ),
              }
            ),
          }))
        );
      })
      .catch(console.error);
  }, [token]);

  useEffect(() => {
    if (!shipment) {
      return;
    }
    form.setFieldsValue({
      courier_tracking_id: shipment.courier_tracking_id ?? '',
    });
  }, [shipment, form]);

  useEffect(() => {
    if (!shipment || couriersList.length === 0) {
      return;
    }
    const courierFk = shipment.courier?.id;
    const match = couriersList.find(
      (c) => String(c.courier ?? c.courier_id) === String(courierFk)
    );
    form.setFieldsValue({
      courier: match?.id,
    });
  }, [shipment, couriersList, form]);

  useEffect(() => {
    if (!shipment) {
      return;
    }
    form.setFieldsValue({
      weight_kg: shipment?.weight_kg,
      volume: shipment?.volume || '0L x 0W x 0H',
      link_code: shipment?.link_code || null,
      quantity: shipment?.product?.quantity,
      viability_window: shipment?.product?.viability_window,
      sensorType: shipment?.sensor?.type,
      teams: shipment?.teams,
      sensor_rows: [{}],
    });
    setSelectedTeams((shipment?.teams as Team[]) || []);
  }, [shipment, form]);

  useEffect(() => {
    if (!shipment || sensorsList.length === 0) {
      return;
    }
    const fromApi = initialSensorRowsFromShipment(shipment, sensorsList);
    const sensorRows: SensorFormRow[] = fromApi.length > 0 ? fromApi : [{}];
    form.setFieldsValue({
      sensor_rows: sensorRows,
      sensorType: shipment?.sensor?.type,
    });
  }, [shipment, sensorsList, form]);

  const resolveCourierFk = useCallback(
    (companyCourierId: string | undefined): string | undefined => {
      if (!companyCourierId) return undefined;
      const match = couriersList.find(
        (c) => String(c.id) === String(companyCourierId)
      );
      return match?.courier ?? match?.courier_id;
    },
    [couriersList]
  );

  const getCarrierTrackingChanges = useCallback(
    (values: Record<string, unknown>) => {
      const companyCourierId = values.courier as string | undefined;
      const newCourierFk = resolveCourierFk(companyCourierId);
      const oldCourierFk = shipment?.courier?.id;
      const newTracking = String(values.courier_tracking_id ?? '').trim();
      const oldTracking = String(shipment?.courier_tracking_id ?? '').trim();
      const courierChanged =
        Boolean(newCourierFk) &&
        String(newCourierFk) !== String(oldCourierFk ?? '');
      const trackingChanged = newTracking !== oldTracking;
      return {
        courierChanged,
        trackingChanged,
        changed: courierChanged || trackingChanged,
        newCourierFk,
        newTracking,
      };
    },
    [resolveCourierFk, shipment]
  );

  const buildSubmitModel = (values: Record<string, unknown>) => {
    // Block only if the user typed something into the location field without
    // selecting/saving a location. An empty field (null state, no display text)
    // means the shipment has no O/D and the user didn't touch it — allow save.
    if (fromDisplayValue && !fromLocation?.id) {
      message.error('Please select a valid origin location or clear the field');
      return null;
    }
    if (toDisplayValue && !toLocation?.id) {
      message.error(
        'Please select a valid destination location or clear the field'
      );
      return null;
    }

    const currentFromId = shipment?.from_location?.id;
    const currentToId = shipment?.to_location?.id;
    // Replace-only: clearing a previously set O/D is not allowed (backend
    // rejects null). Block save so the change is not silently dropped.
    if (currentFromId && !fromLocation?.id) {
      message.error('Origin cannot be cleared. Select a replacement location.');
      return null;
    }
    if (currentToId && !toLocation?.id) {
      message.error(
        'Destination cannot be cleared. Select a replacement location.'
      );
      return null;
    }

    const model: Record<string, unknown> = { ...values };
    delete model.from_location;
    delete model.to_location;

    if (shipmentId) {
      model.shipment_id = shipmentId;
    }

    if (
      fromLocation?.id &&
      String(fromLocation.id) !== String(currentFromId ?? '')
    ) {
      model.from_location = fromLocation.id;
    }
    if (toLocation?.id && String(toLocation.id) !== String(currentToId ?? '')) {
      model.to_location = toLocation.id;
    }
    const teams = (values.teams as Team[] | undefined) || [];
    model.teams = teams.map((t) => t.id);

    if (linksEnabled && links.sensorCardOpen) {
      message.error("Save or cancel the sensor you're editing");
      return null;
    }
    delete model.sensor_rows;
    delete model.sensor;
    delete model.sensor_tracking_id;
    const sensorsPayload = editSensorsPayload({
      rows: linksEnabled
        ? links.sensorRows
        : (values.sensor_rows as SensorFormRow[] | undefined),
      sensorsList,
      includeProductIds: linksEnabled && links.sendProductIds(),
      sensorRequired: Boolean(values.sensorType),
    });
    if (sensorsPayload.ok === false) {
      message.error(sensorsPayload.message);
      return null;
    }
    if (sensorsPayload.warning) message.warning(sensorsPayload.warning);
    if (sensorsPayload.sensors) model.sensors = sensorsPayload.sensors;
    const productLines = linksEnabled ? links.products.payload() : undefined;
    if (productLines) {
      model.products = productLines;
    }

    const savedFrom = {
      latitude: shipment?.from_location?.latlng?.lat,
      longitude: shipment?.from_location?.latlng?.lng,
    };
    const savedTo = {
      latitude: shipment?.to_location?.latlng?.lat,
      longitude: shipment?.to_location?.latlng?.lng,
    };
    if (
      createInTive &&
      !(
        hasCoordinates(fromLocation, savedFrom) &&
        hasCoordinates(toLocation, savedTo)
      )
    ) {
      message.error(TIVE_ADDRESS_REQUIRED_MESSAGE);
      return null;
    }

    const dateFormat = decodedToken?.date_format as string | undefined;
    for (const pair of SHIPMENT_DATE_FORM_PAIRS) {
      delete model[pair.dateKey];
      delete model[pair.timeKey];
    }
    const dateEdits = applyShipmentDateEdits(
      values,
      shipment ?? {},
      dateFormat
    );
    if (dateEdits.ok === false) {
      message.error(dateEdits.message);
      return null;
    }
    Object.assign(model, dateEdits.fields);

    return model;
  };

  const runUpdateShipment = (model: Record<string, unknown>) => {
    showSpin();
    UpdateShipment(token ?? '', model)
      .then((res) => {
        const data = res?.data || null;
        if (data) {
          if (data?.data?.is_success) {
            message.success(data?.data?.message);
            if (linksEnabled) {
              links.afterSave();
            }
            if (createInTive) {
              runTiveAutocreate(token ?? '', String(shipmentId)).catch(
                console.error
              );
            }
            // Keep the side panel open on the detail view after save.
            toggleDrawer(true, 'Detail');
            onEditShipmentSuccess(data?.data?.data);
          } else {
            message.error(data?.data?.message);
          }
        }
      })
      .catch((error) => {
        unauthorize(error);
      })
      .finally(() => {
        hideSpin();
      });
  };

  const executeSubmit = async (model: Record<string, unknown>) => {
    const isShell = Boolean(shipment?.is_shell);
    const {
      changed,
      courierChanged,
      trackingChanged,
      newCourierFk,
      newTracking,
    } = getCarrierTrackingChanges(model);

    const trackModel = { ...model };

    if (!isShell && changed) {
      const reassignPayload: {
        courier_id?: string;
        courier_tracking_id?: string;
      } = {};
      if (courierChanged && newCourierFk) {
        reassignPayload.courier_id = String(newCourierFk);
      }
      if (trackingChanged) {
        reassignPayload.courier_tracking_id = newTracking;
      }

      showSpin();
      try {
        const reassignRes = await ReassignShipmentCarrier(
          token ?? '',
          shipmentId,
          reassignPayload
        );
        const wrapped = reassignRes?.data;
        if (!wrapped?.data?.is_success) {
          message.error(wrapped?.data?.message ?? 'Update failed');
          return;
        }

        delete trackModel.courier;
        delete trackModel.courier_tracking_id;
        // Reassign clears delivery fields; don't restore from stale form values
        // (delivered shipments pre-fill the delivery date pickers on load).
        delete trackModel.actual_delivery_date;
        delete trackModel.actual_pickup_date;
        delete trackModel.updated_delivery_date;
        delete trackModel.updated_delivery_date_time;
        delete trackModel.updated_pickup_date;
        delete trackModel.updated_pickup_date_time;
      } catch (error) {
        // Axios rejects with the response-shaped error unauthorize() reads.
        unauthorize(error as ErrorLike);
        return;
      } finally {
        hideSpin();
      }
    }

    const courierFk = resolveCourierFk(
      trackModel.courier as string | undefined
    );
    if (courierFk) {
      trackModel.courier = courierFk;
    }
    runUpdateShipment(trackModel);
  };

  const onFinish = (values: Record<string, unknown>) => {
    if (isArchived) {
      message.error('Archived shipments cannot be edited');
      return;
    }
    if (!values || Object.keys(values).length === 0) {
      return;
    }
    const model = buildSubmitModel(values);
    if (!model) {
      return;
    }

    const isShell = Boolean(shipment?.is_shell);
    const { changed } = getCarrierTrackingChanges(values);

    if (!isShell && changed) {
      setPendingSubmitModel(model);
      setReassignConfirmOpen(true);
      return;
    }

    executeSubmit(model).catch(console.error);
  };

  const handleConfirmReassign = () => {
    setReassignConfirmOpen(false);
    if (pendingSubmitModel) {
      executeSubmit(pendingSubmitModel).catch(console.error);
      setPendingSubmitModel(null);
    }
  };

  const tiveOption = canCreateInTive ? (
    <div className={styles.tiveOption}>
      <CustomCheckboxGroup
        options={[
          {
            label: (
              <span className={styles.tiveOptionLabel}>
                Create shipment in Tive
              </span>
            ),
            value: 'Create shipment in Tive',
          },
        ]}
        selectedValues={createInTive ? ['Create shipment in Tive'] : []}
        onChange={(values) => {
          setCreateInTive((values as string[]).length > 0);
        }}
      />
    </div>
  ) : null;

  const submitEdit = (): void => {
    if (isArchived) {
      message.error('Archived shipments cannot be edited');
      return;
    }
    form.submit();
  };

  const courierField = (
    <ConfigProvider theme={FLUSH_FORM_ITEM}>
      <Form.Item
        label={`${theme.themeType === 2 ? 'Carrier' : 'Transport provider'}`}
        name='courier'
      >
        <CustomDynamicSelect
          placeholder='Select'
          height='36px'
          border='0px'
          bgColor='var(--light-gray)'
          textColor='var(--text-primary)'
          borderRadius='8px'
          options={couriersList}
          label='courier_label'
          allowClear={true}
        />
      </Form.Item>
    </ConfigProvider>
  );

  const trackingField = (
    <ConfigProvider theme={FLUSH_FORM_ITEM}>
      <Form.Item label='Tracking ID' name='courier_tracking_id'>
        <CustomInput
          placeholder='Enter tracking ID'
          height='36px'
          bgColor='var(--light-gray)'
          border='0px'
          borderRadius='8px'
        />
      </Form.Item>
    </ConfigProvider>
  );

  const teamsField = hasValidTeams ? (
    <Row justify='space-between'>
      <Col span={24}>
        <Form.Item
          className='custom-input'
          label={
            <span className='form-label-12-600'>{`${themeT('team', theme.themeType)}s`}</span>
          }
          name='teams'
        >
          <TeamDropdownSelect
            teamsOptions={allTeams}
            selectedTeams={selectedTeams}
            onChange={(teams) => {
              setSelectedTeams(teams);
              form.setFieldsValue({ teams });
            }}
            formState='edit'
            onSearch={handleTeamsSearch}
            teamsSearch={teamsSearch}
          />
        </Form.Item>
      </Col>
    </Row>
  ) : null;

  return (
    <>
      <Flex
        gap={16}
        justify='space-between'
        align='center'
        hidden={drawerView !== 'shipment'}
      >
        <span
          className={`c-pointer ${styles.backIcon}`}

          onClick={() => toggleDrawer(true, 'Detail')}
        >
          <LeftTailArrowIcon color='var(--text-primary)' />
        </span>
        <span
          title='Close'
          className='no-bg c-pointer'
          onClick={() => toggleDrawer(false)}
        >
          <CloseOutlined
            color='var(--text-primary)'
            style={{ fontSize: '14px', color: 'var(--text-primary)' }}
          />
        </span>
      </Flex>

      <Form
        hidden={drawerView !== 'shipment'}
        form={form}
        name='CreateNewShipment'
        layout='vertical'
        onFinish={onFinish}
        onValuesChange={(changedValues) => {
          if ('sensor_rows' in changedValues) {
            const rows = (form.getFieldValue('sensor_rows') ||
              []) as SensorFormRow[];
            if (rows.length > 0) {
              const trackingIdFieldPaths = rows.map((_, index) => [
                'sensor_rows',
                index,
                'sensor_tracking_id',
              ]);
              form.validateFields(trackingIdFieldPaths).catch(() => {});
            }
          }
        }}
        autoComplete='off'
        disabled={isArchived}
      >
        <Flex vertical={true} gap={16}>
          <Flex gap={16} justify='space-between' className='edit-drawer-header'>
            <Flex vertical={true}>
              <CustomTitle
                text='Edit shipment'
                fontSize='15px'
                color='var(--text-primary)'
                fontWeight={700}
                lineHeight={'28px'}
                marginBottom={'0px'}
              />
              <CustomTitle
                text={'Changes will be logged in the chain of custody'}
                fontSize='12px'
                fontWeight={400}
                lineHeight='20px'
                marginTop={'0px'}
                color='var(--text-secondary)'
                marginBottom='0'
              />
            </Flex>

            <Flex gap={8} hidden={linksEnabled}>
              <CustomButton
                text={'Save changes'}
                height='28px'
                fontSize='12px'
                fontWeight='600'
                lineHeight='16px'
                borderRadius='8px'
                bgColor='#000000'
                color='#ffffff'
                boxShadow='none'
                padding='0 12px'
                isFormValid={isArchived}
                onClick={submitEdit}
              />
            </Flex>
          </Flex>

          {linksEnabled && teamsField}
          {linksEnabled && <hr className={styles.sectionRule} />}
          <Flex vertical={true} gap={16}>
            <Flex justify='space-between' align='center' gap={16}>
              <CustomTitle
                text={'Shipment details'}
                fontSize='15px'
                fontWeight={700}
                lineHeight='28px'
                color='var(--text-primary)'
                marginBottom='0'
              />
            </Flex>

            <Row justify='space-between'>
              <Col span={11}>
                <GoogleMapsProvider>
                  <LocationAutoCompleteShipment
                    locationList={locationList}
                    handleAddressPlaceSelect={(place, _field, selectOpts) => {
                      handleAddressPlaceSelect(
                        place as LocationOption & { address?: string },
                        'from_location',
                        selectOpts
                      );
                    }}
                    fieldName='from_location'
                    label='Origin'
                    width='100%'
                    className='custom-input form-input-style'
                    placeholder='Enter pick-up address/location'
                    externalValue={fromDisplayValue}
                    onInputChange={(val) =>
                      handleAddressInputChange('from', val)
                    }
                    isSaved={
                      !!(fromLocation?.id && !fromLocation?.placeName) &&
                      !fromEditMode
                    }
                    onSavedClick={() => setFromEditMode(true)}
                    suffixIcon={
                      canCreateLocation && fromDisplayValue ? (
                        fromLocation?.id && !fromLocation?.placeName ? (
                          <svg
                            className={styles.clearIcon}
                            width='11'
                            height='14'
                            viewBox='0 0 14 18'
                            fill='var(--text-primary)'

                            onClick={() => handleDetachLocation('from')}
                          >
                            <path d='M1 1h12v16l-6-4-6 4V1z' />
                          </svg>
                        ) : (
                          <svg
                            className={styles.clearIcon}
                            width='11'
                            height='14'
                            viewBox='0 0 14 18'
                            fill='none'
                            stroke='#888'
                            strokeWidth='1.5'

                            onClick={() => handleBookmarkClick('from')}
                          >
                            <path
                              d='M1 1h12v16l-6-4-6 4V1z'
                              strokeLinejoin='round'
                            />
                          </svg>
                        )
                      ) : undefined
                    }
                  />
                </GoogleMapsProvider>
              </Col>

              <Col span={11}>
                <GoogleMapsProvider>
                  <LocationAutoCompleteShipment
                    locationList={locationList}
                    handleAddressPlaceSelect={(place, _field, selectOpts) =>
                      handleAddressPlaceSelect(
                        place as LocationOption & { address?: string },
                        'to_location',
                        selectOpts
                      )
                    }
                    fieldName='to_location'
                    label='Destination'
                    width='100%'
                    className='label-13px'
                    placeholder='Enter delivery address/location'
                    externalValue={toDisplayValue}
                    onInputChange={(val) => handleAddressInputChange('to', val)}
                    isSaved={
                      !!(toLocation?.id && !toLocation?.placeName) &&
                      !toEditMode
                    }
                    onSavedClick={() => setToEditMode(true)}
                    suffixIcon={
                      canCreateLocation && toDisplayValue ? (
                        toLocation?.id && !toLocation?.placeName ? (
                          <svg
                            className={styles.clearIcon}
                            width='11'
                            height='14'
                            viewBox='0 0 14 18'
                            fill='var(--text-primary)'

                            onClick={() => handleDetachLocation('to')}
                          >
                            <path d='M1 1h12v16l-6-4-6 4V1z' />
                          </svg>
                        ) : (
                          <svg
                            className={styles.clearIcon}
                            width='11'
                            height='14'
                            viewBox='0 0 14 18'
                            fill='none'
                            stroke='#888'
                            strokeWidth='1.5'

                            onClick={() => handleBookmarkClick('to')}
                          >
                            <path
                              d='M1 1h12v16l-6-4-6 4V1z'
                              strokeLinejoin='round'
                            />
                          </svg>
                        )
                      ) : undefined
                    }
                  />
                </GoogleMapsProvider>
              </Col>
            </Row>

            <Row justify='space-between'>
              <Col span={11}>
                <CustomShipmentText
                  text={'Scheduled pickup'}
                  fontWeight={600}
                  marginBottom='4px'
                />
                <Form.Item shouldUpdate noStyle>
                  {({ getFieldValue }) => {
                    const date = getFieldValue('updated_scheduled_pickup_date');
                    const time = getFieldValue(
                      'updated_scheduled_pickup_date_time'
                    );
                    const hasError = (date && !time) || (!date && time);
                    return (
                      <>
                        <Space>
                          <Form.Item
                            name='updated_scheduled_pickup_date'
                            noStyle
                          >
                            <CustomDatePicker
                              value={date}
                              onChange={(newDate) => {
                                form.setFieldsValue({
                                  updated_scheduled_pickup_date: newDate,
                                });
                              }}
                              timeZone={timeZone}
                            />
                          </Form.Item>
                          <Form.Item
                            name='updated_scheduled_pickup_date_time'
                            noStyle
                          >
                            <CustomTimePicker
                              value={time}
                              onChange={(newTime) => {
                                form.setFieldsValue({
                                  updated_scheduled_pickup_date_time: newTime,
                                });
                              }}
                              timeZone={timeZone}
                              showNow={false}
                            />
                          </Form.Item>
                        </Space>
                        {hasError && (
                          <div className={styles.fieldError}>
                            Please select both date and time
                          </div>
                        )}
                      </>
                    );
                  }}
                </Form.Item>
              </Col>

              <Col span={11}>
                <CustomShipmentText
                  text={'Scheduled delivery'}
                  fontWeight={600}
                  marginBottom='4px'
                />
                <Form.Item shouldUpdate noStyle>
                  {({ getFieldValue }) => {
                    const date = getFieldValue(
                      'updated_scheduled_delivery_date'
                    );
                    const time = getFieldValue(
                      'updated_scheduled_delivery_date_time'
                    );
                    const hasError = (date && !time) || (!date && time);
                    return (
                      <>
                        <Space>
                          <Form.Item
                            name='updated_scheduled_delivery_date'
                            noStyle
                          >
                            <CustomDatePicker
                              value={date}
                              onChange={(newDate) => {
                                form.setFieldsValue({
                                  updated_scheduled_delivery_date: newDate,
                                });
                              }}
                              timeZone={timeZone}
                            />
                          </Form.Item>
                          <Form.Item
                            name='updated_scheduled_delivery_date_time'
                            noStyle
                          >
                            <CustomTimePicker
                              value={time}
                              onChange={(newTime) => {
                                form.setFieldsValue({
                                  updated_scheduled_delivery_date_time: newTime,
                                });
                              }}
                              timeZone={timeZone}
                              showNow={false}
                            />
                          </Form.Item>
                        </Space>
                        {hasError && (
                          <div className={styles.fieldError}>
                            Please select both date and time
                          </div>
                        )}
                      </>
                    );
                  }}
                </Form.Item>
              </Col>
            </Row>

            <Row justify='space-between'>
              <Col span={11}>
                <CustomShipmentText
                  text={'Picked up'}
                  fontWeight={600}
                  marginBottom='4px'
                />
                <Form.Item shouldUpdate noStyle>
                  {({ getFieldValue }) => {
                    const date = getFieldValue('updated_pickup_date');
                    const time = getFieldValue('updated_pickup_date_time');
                    const hasError = (date && !time) || (!date && time);
                    return (
                      <>
                        <Space>
                          <Form.Item name='updated_pickup_date' noStyle>
                            <CustomDatePicker
                              value={date}
                              onChange={(newDate) => {
                                form.setFieldsValue({
                                  updated_pickup_date: newDate,
                                });
                              }}
                              timeZone={timeZone}
                            />
                          </Form.Item>
                          <Form.Item name='updated_pickup_date_time' noStyle>
                            <CustomTimePicker
                              value={time}
                              onChange={(newTime) => {
                                form.setFieldsValue({
                                  updated_pickup_date_time: newTime,
                                });
                              }}
                              timeZone={timeZone}
                              showNow={false}
                            />
                          </Form.Item>
                        </Space>
                        {hasError && (
                          <div className={styles.fieldError}>
                            Please select both date and time
                          </div>
                        )}
                      </>
                    );
                  }}
                </Form.Item>
              </Col>

              <Col span={11}>
                <CustomShipmentText
                  text={'Delivered'}
                  fontWeight={600}
                  marginBottom='4px'
                />
                <Form.Item shouldUpdate noStyle>
                  {({ getFieldValue }) => {
                    const date = getFieldValue('updated_delivery_date');
                    const time = getFieldValue('updated_delivery_date_time');
                    const hasError = (date && !time) || (!date && time);
                    return (
                      <>
                        <Space>
                          <Form.Item name='updated_delivery_date' noStyle>
                            <CustomDatePicker
                              value={date}
                              onChange={(newDate) => {
                                form.setFieldsValue({
                                  updated_delivery_date: newDate,
                                });
                              }}
                              timeZone={timeZone}
                            />
                          </Form.Item>
                          <Form.Item name='updated_delivery_date_time' noStyle>
                            <CustomTimePicker
                              value={time}
                              onChange={(newTime) => {
                                form.setFieldsValue({
                                  updated_delivery_date_time: newTime,
                                });
                              }}
                              timeZone={timeZone}
                              showNow={false}
                            />
                          </Form.Item>
                        </Space>
                        {hasError && (
                          <div className={styles.fieldError}>
                            Please select both date and time
                          </div>
                        )}
                      </>
                    );
                  }}
                </Form.Item>
              </Col>
            </Row>

            <Row justify='space-between'>
              <Col span={11}>
                <CustomShipmentText
                  text={'Total weight (kg)'}
                  fontWeight={600}
                  marginBottom='4px'
                />
                <Form.Item
                  className='custom-input'
                  name='weight_kg'
                  rules={[
                    { required: true, message: 'Please enter total weight' },
                  ]}
                  style={{ marginBottom: '0px' }}
                >
                  <CustomInput
                    placeholder='Enter weight'
                    height='36px'
                    width='100%'
                    border='0px'
                    bgColor='var(--light-gray)'
                    textColor='var(--text-primary)'
                    borderRadius='8px'
                    placeholderColor='var(--text-secondary)'
                    lineHeight='38px'
                  />
                </Form.Item>
              </Col>

              <Col span={11}>
                <CustomShipmentText
                  text={'Dimensions'}
                  fontWeight={600}
                  marginBottom='4px'
                />

                <Form.Item
                  className='custom-input'
                  name='volume'
                  rules={[
                    { required: true, message: 'Please enter dimensions' },
                    {
                      pattern:
                        /^\d+(\.\d+)?\s*L\s*x\s*\d+(\.\d+)?\s*W\s*x\s*\d+(\.\d+)?\s*H$/,
                      message: 'Format must be like: 200L x 100W x 150H',
                    },
                  ]}
                  style={{ marginBottom: '0px' }}
                >
                  <CustomInput
                    placeholder='(200 L x 100 W x 150 H)'
                    height='36px'
                    width='100%'
                    border='0px'
                    bgColor='var(--light-gray)'
                    textColor='var(--text-primary)'
                    borderRadius='8px'
                    placeholderColor='var(--text-secondary)'
                    lineHeight='38px'
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row justify='space-between'>
              <Col span={11} style={{ marginTop: '1px' }}>
                <CustomShipmentText
                  text={themeT('referenceId', theme.themeType)}
                  fontWeight={600}
                  marginBottom='4px'
                />
                <TruncateWithTooltip
                  text={shipment?.reference_number}
                  max={40}
                  render={(t) => (
                    <span>
                      {' '}
                      <CustomShipmentText text={t} color='var(--text-light)' />
                    </span>
                  )}
                />
              </Col>

              <Col span={11}>
                <CustomShipmentText
                  text={`${themeT('linkCode', theme.themeType)} (optional)`}
                  fontWeight={600}
                  marginBottom='4px'
                />

                <Form.Item
                  className='custom-input'
                  name='link_code'
                  rules={[
                    {
                      required: false,
                      pattern: /^[a-zA-Z0-9]{0,255}$/,
                      message: 'Please enter up to 255 alphanumeric characters',
                    },
                  ]}
                  style={{ marginBottom: '0px' }}
                >
                  <CustomInput
                    placeholder='Code'
                    height='36px'
                    width='100%'
                    border='0px'
                    bgColor='var(--light-gray)'
                    textColor='var(--text-primary)'
                    borderRadius='8px'
                    placeholderColor='var(--text-secondary)'
                    lineHeight='38px'
                  />
                </Form.Item>
              </Col>
            </Row>
            {!linksEnabled && teamsField}

            {linksEnabled ? (
              <EditProductsAndSensors
                key={shipmentId}
                links={links}
                sensorsList={sensorsList}
                sensorOptions={tiveOption}
              />
            ) : (
              <>
                <Flex justify='space-between' align='center' gap={16}>
                  <CustomTitle
                    text={'Products details'}
                    fontSize='15px'
                    fontWeight={700}
                    lineHeight='28px'
                    color='var(--text-primary)'
                    marginBottom='0'
                  />
                </Flex>

                <Row justify='space-between'>
                  {/* product name  */}
                  <Col span={11}>
                    <CustomShipmentText
                      text={'Product'}
                      fontWeight={600}
                      marginBottom='4px'
                    />
                    <CustomShipmentText
                      text={shipment?.product?.name}
                      noText='--'
                      color='var(--text-light)'
                    />
                  </Col>
                  {/* quantity  */}
                  <Col span={11}>
                    <CustomShipmentText
                      text={'Product quantity'}
                      fontWeight={600}
                      marginBottom='4px'
                    />
                    <CustomShipmentText
                      text={shipment?.product?.quantity?.toString()}
                      noText='--'
                      color='var(--text-light)'
                    />
                  </Col>
                </Row>

                <Row justify='space-between'>
                  {/* product type  */}
                  <Col span={11}>
                    <CustomShipmentText
                      text={'Product type'}
                      fontWeight={600}
                      marginBottom='4px'
                    />
                    <CustomShipmentText
                      text={shipment?.product?.product_type}
                      noText='--'
                      color='var(--text-light)'
                    />
                  </Col>
                  {/* project ID  */}
                  <Col span={11}>
                    <Tooltip
                      title={shipment?.product?.project_id}
                      placement='top'
                    >
                      <span>
                        <CustomShipmentText
                          text={'Project ID'}
                          fontWeight={600}
                          marginBottom='4px'
                        />
                        <CustomShipmentText
                          text={
                            shipment?.product?.project_id
                              ? (() => {
                                  const projectIds =
                                    shipment.product.project_id.split(',');
                                  return projectIds.length > 2
                                    ? `${projectIds.slice(0, 2).join(', ')}, +${
                                        projectIds.length - 2
                                      }`
                                    : projectIds.join(', ');
                                })()
                              : '--'
                          }
                          noText='--'
                          color='var(--text-light)'
                        />
                      </span>
                    </Tooltip>
                  </Col>
                </Row>
                <Row justify='space-between'>
                  {/* Temperature range */}
                  <Col span={11}>
                    <Tooltip
                      title={
                        <div className={styles.tooltipText}>
                          Temperature range overlap for all products
                        </div>
                      }
                      overlayStyle={{ maxWidth: '154px', textAlign: 'center' }}
                      placement='top'
                    >
                      <CustomShipmentText
                        text={'Temperature range'}
                        fontWeight={600}
                        marginBottom='4px'
                      />
                      <CustomShipmentText
                        text={shipment?.product?.temperature_range}
                        noText='--'
                        color='var(--text-light)'
                      />
                    </Tooltip>
                  </Col>
                  {/* Transit viability window */}
                  <Col span={11}>
                    <CustomShipmentText
                      text={'Transit viability window'}
                      fontWeight={600}
                      marginBottom='4px'
                    />
                    <CustomShipmentText
                      text={
                        shipment?.product?.viability_window
                          ? shipment?.product?.viability_window + ' hrs'
                          : '--'
                      }
                      color='var(--text-light)'
                    />
                  </Col>
                </Row>

                {/* sensors (multi-row → API `sensors`; sensorType set from shipment in useEffect) */}
                <Col span={24}>
                  <CustomShipmentText
                    text={'Sensors'}
                    fontWeight={600}
                    marginBottom='8px'
                  />
                  <Form.List name='sensor_rows'>
                    {(fields, { add, remove }) => (
                      <>
                        {fields.map((field, index) => (
                          <div className={styles.sensorRow} key={field.key}>
                            <Flex
                              justify='space-between'
                              align='center'
                              style={{ marginBottom: '6px' }}
                            >
                              <CustomShipmentText
                                text={
                                  index === 0
                                    ? 'Primary sensor'
                                    : `Sensor ${index + 1}`
                                }
                                fontWeight={600}
                                marginBottom='0'
                                color='var(--text-primary)'
                              />
                              {fields.length > 1 ? (
                                <span
                                  role='button'
                                  tabIndex={0}
                                  onClick={() => {
                                    remove(field.name);
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      (e.currentTarget as HTMLElement).click();
                                    }
                                  }}
                                  className={styles.removeIcon}
                                  aria-label='Remove sensor row'
                                >
                                  <DeleteIcon />
                                </span>
                              ) : null}
                            </Flex>
                            <Flex vertical gap={4}>
                              <Form.Item
                                className='custom-input'
                                name={[field.name, 'sensor']}
                                style={{ marginBottom: '8px' }}
                                rules={[
                                  ({ getFieldValue }) => ({
                                    validator: (_, value) => {
                                      const tid = getFieldValue([
                                        'sensor_rows',
                                        field.name,
                                        'sensor_tracking_id',
                                      ]);
                                      if (
                                        tid &&
                                        typeof tid === 'string' &&
                                        tid.trim() &&
                                        !value
                                      ) {
                                        return Promise.reject(
                                          new Error('Please select sensor')
                                        );
                                      }
                                      return Promise.resolve();
                                    },
                                  }),
                                ]}
                              >
                                <CustomDynamicSelect
                                  placeholder='Sensor provider'
                                  width='100%'
                                  height='36px'
                                  border='0px'
                                  bgColor='var(--light-gray)'
                                  textColor='var(--text-secondary)'
                                  borderRadius='8px'
                                  options={sensorsList}
                                  label='sensor_label'
                                />
                              </Form.Item>
                              <Form.Item
                                className='custom-input'
                                name={[field.name, 'sensor_tracking_id']}
                                style={{ marginBottom: '0px' }}
                                rules={[
                                  {
                                    validator: (_, value) => {
                                      if (
                                        value &&
                                        typeof value === 'string' &&
                                        value.trim() === ''
                                      ) {
                                        return Promise.reject(
                                          new Error(
                                            'Only spaces are not allowed'
                                          )
                                        );
                                      }
                                      return Promise.resolve();
                                    },
                                  },
                                  ({ getFieldValue }) => ({
                                    validator: (_, value) => {
                                      const provider = getFieldValue([
                                        'sensor_rows',
                                        field.name,
                                        'sensor',
                                      ]);
                                      if (provider && !value) {
                                        return Promise.reject(
                                          new Error('Please enter sensor id')
                                        );
                                      }
                                      return Promise.resolve();
                                    },
                                  }),
                                  ({ getFieldValue }) => ({
                                    validator: (_, value) => {
                                      if (
                                        !value ||
                                        typeof value !== 'string' ||
                                        !value.trim()
                                      ) {
                                        return Promise.resolve();
                                      }
                                      const currentTid = value
                                        .replace(/\s+/g, '')
                                        .toLowerCase();
                                      const rows = (getFieldValue(
                                        'sensor_rows'
                                      ) || []) as SensorFormRow[];
                                      const duplicateMatches = rows.filter(
                                        (r) =>
                                          r?.sensor_tracking_id &&
                                          typeof r.sensor_tracking_id ===
                                            'string' &&
                                          r.sensor_tracking_id
                                            .replace(/\s+/g, '')
                                            .toLowerCase() === currentTid
                                      );
                                      if (duplicateMatches.length > 1) {
                                        return Promise.reject(
                                          new Error(
                                            'Duplicate sensor tracking ID is not allowed'
                                          )
                                        );
                                      }
                                      return Promise.resolve();
                                    },
                                  }),
                                ]}
                              >
                                <CustomInput
                                  placeholder='Sensor ID'
                                  height='36px'
                                  width='100%'
                                  border='0px'
                                  bgColor='var(--light-gray)'
                                  textColor='var(--text-primary)'
                                  borderRadius='8px'
                                  placeholderColor='var(--text-secondary)'
                                  lineHeight='38px'
                                />
                              </Form.Item>
                            </Flex>
                            {index < fields.length - 1 ? (
                              <Divider style={{ margin: '12px 0' }} />
                            ) : null}
                          </div>
                        ))}
                        <AddItemButton
                          text='Add another sensor'
                          fontSize='12px'
                          fontWeight='600'
                          marginTop='4px'
                          onClick={() => add({})}
                        />
                      </>
                    )}
                  </Form.List>
                  {tiveOption}
                </Col>
              </>
            )}

            {linksEnabled && <hr className={styles.sectionRule} />}
            {/* Transport provider  */}
            <Flex justify='space-between' align='center' gap={16}>
              <CustomTitle
                text={`${theme.themeType === 2 ? 'Carrier' : 'Transport provider'}`}
                fontSize='15px'
                fontWeight={700}
                lineHeight='28px'
                color='var(--text-primary)'
                marginBottom='0'
              />
            </Flex>

            {linksEnabled ? (
              <Row justify='space-between'>
                <Col span={11}>{courierField}</Col>
                <Col span={11}>{trackingField}</Col>
              </Row>
            ) : (
              <>
                <Row justify='space-between' className={styles.transportRow}>
                  <Col span={24}>{courierField}</Col>
                </Row>
                <Row justify='space-between' className={styles.transportRow}>
                  <Col span={24}>{trackingField}</Col>
                </Row>
              </>
            )}

            {!linksEnabled && !shipment?.is_shell && (
              <>
                <Row justify='space-between' style={{ marginBottom: 16 }}>
                  <Col span={11}>
                    <CustomShipmentText
                      text={'Service level'}
                      fontWeight={600}
                      marginBottom='4px'
                    />
                    <CustomShipmentText
                      text={shipment?.courier_service_level || '--'}
                      color='var(--text-light)'
                    />
                  </Col>
                  <Col span={11}>
                    <CustomShipmentText
                      text={`${theme.themeType === 2 ? 'Carrier' : 'Transport provider'} ETA`}
                      fontWeight={600}
                      marginBottom='4px'
                    />
                    <CustomShipmentText
                      text={formatDateWithTZ(shipment?.transport_provider_eta)}
                      noText='--'
                      color='var(--text-light)'
                    />
                  </Col>
                </Row>
              </>
            )}
            {linksEnabled && (
              <EditShipmentFooter
                onSave={submitEdit}
                onCancel={() => toggleDrawer(true, 'Detail')}
                disabled={isArchived}
              />
            )}
          </Flex>
        </Flex>
      </Form>

      {linksEnabled && drawerView === 'product_form' && (
        <EditProductFormView
          onDone={(created) => {
            links.finishAddProduct(created);
            setDrawerView('shipment');
          }}
        />
      )}

      <SaveLocationModal
        open={saveLocationModalOpen}
        address={
          saveLocationTarget === 'from'
            ? fromDisplayValue || ''
            : toDisplayValue || ''
        }
        onSave={handleSaveLocation}
        onCancel={() => setSaveLocationModalOpen(false)}
      />

      <CustomDeleteModal
        visible={reassignConfirmOpen}
        onClose={() => {
          setReassignConfirmOpen(false);
          setPendingSubmitModel(null);
        }}
        onConfirm={handleConfirmReassign}
        title='Update transport provider & tracking?'
        message='This will archive all existing status updates and clear pickup/delivery dates while new tracking events come in.'
        cancelText='Cancel'
        confirmText='Yes, update'
        confirmColor='var(--text-primary)'
      />
    </>
  );
};

export default EditShipmentDrawerWithTeams;
